import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Menu, X } from 'lucide-react';
import { useCopy } from '@/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';

type NavCopy = {
  how_it_works: string;
  situations: string;
  pricing: string;
  sign_in: string;
  blog?: string;
  contact?: string;
};

// Fallback navigation labels if locale JSON is still loading.
const FALLBACK_NAV: NavCopy = {
  how_it_works: 'How it works',
  situations: 'Situations',
  pricing: 'Pricing',
  sign_in: 'Sign in',
  blog: 'Blog',
  contact: 'Contact',
};

export function Nav({ ctaPrimary }: { ctaPrimary: string }) {
  const copy = useCopy();
  const n = copy<NavCopy>('nav') ?? FALLBACK_NAV;
  const reduceMotion = useReducedMotion();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock page scroll while the mobile overlay is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const links = [
    { label: n.how_it_works ?? FALLBACK_NAV.how_it_works, href: '#how' },
    { label: n.situations ?? FALLBACK_NAV.situations, href: '#situations' },
    { label: n.pricing ?? FALLBACK_NAV.pricing, href: '#pricing' },
    { label: n.blog ?? FALLBACK_NAV.blog!, href: '/blog' },
  ];
  const mobileLinks = [...links, { label: n.contact ?? FALLBACK_NAV.contact!, href: '/contact' }];

  const brand = (
    <a
      href="/"
      className="marketing-nav__brand"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <img
        src="/WIMLogo-2-200px.png"
        alt=""
        aria-hidden="true"
        className="marketing-nav__logo"
        style={{ height: 32, width: 'auto', flexShrink: 0 }}
      />
      <span
        className="marketing-nav__wordmark"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 26,
          fontWeight: 500,
          letterSpacing: '-0.02em',
        }}
      >
        whatimean
      </span>
      <span style={{ color: 'var(--coral)', fontFamily: 'var(--font-display)', fontSize: 26 }}>
        .
      </span>
    </a>
  );

  return (
    <>
      <nav className={scrolled ? 'marketing-nav marketing-nav--scrolled' : 'marketing-nav'}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: 1480,
            margin: '0 auto',
            gap: 16,
          }}
        >
          {brand}
          <div
            className="marketing-nav__links nav-links"
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}
          >
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-md px-3.5 py-2 transition-colors hover:bg-[color-mix(in_srgb,var(--paper)_12%,transparent)]"
              >
                {link.label}
              </a>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LanguageSwitcher tone="inverse" />
            <a className="marketing-nav__signin nav-signin" href="/login" style={{ fontSize: 14 }}>
              {n.sign_in ?? FALLBACK_NAV.sign_in}
            </a>
            {ctaPrimary ? (
              <a
                href="#pricing"
                className="btn btn-primary btn-arrow"
                style={{ padding: '10px 18px', fontSize: 14 }}
              >
                {ctaPrimary}
              </a>
            ) : null}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="marketing-nav__menu-btn flex h-10 w-10 items-center justify-center rounded-md md:hidden"
              style={{
                background: 'transparent',
                border: '1px solid var(--line)',
                color: 'inherit',
              }}
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay (adapted from reactbits-pro navigation-7) */}
      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.25 }}
            className="fixed inset-0 z-50 flex flex-col md:hidden"
            style={{ background: 'var(--paper)', color: 'var(--ink)' }}
          >
            <div
              className="flex items-center justify-between"
              style={{ padding: '18px var(--wrap-px)' }}
            >
              {brand}
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-md"
                style={{ background: 'var(--paper-2)', border: '1px solid var(--line)' }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <nav
              className="flex-1 overflow-y-auto"
              style={{ padding: '8px var(--wrap-px)' }}
              aria-label="Mobile navigation"
            >
              <div className="space-y-2">
                {mobileLinks.map((link, index) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    initial={reduceMotion ? false : { opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: reduceMotion ? 0 : 0.25,
                      delay: reduceMotion ? 0 : index * 0.05,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    className="block rounded-md px-4 py-3 text-lg font-medium"
                    style={{
                      background: 'var(--paper-2)',
                      border: '1px solid var(--line)',
                      fontFamily: 'var(--font-display)',
                    }}
                  >
                    {link.label}
                  </motion.a>
                ))}
              </div>
              <div className="mt-6">
                <LanguageSwitcher />
              </div>
            </nav>

            <div
              className="space-y-3"
              style={{
                padding: '16px var(--wrap-px) max(16px, env(safe-area-inset-bottom))',
                borderTop: '1px solid var(--line)',
              }}
            >
              <a
                href="/login"
                onClick={() => setMenuOpen(false)}
                className="block w-full rounded-md px-4 py-3 text-center text-sm font-medium"
                style={{ background: 'var(--paper-2)', border: '1px solid var(--line)' }}
              >
                {n.sign_in ?? FALLBACK_NAV.sign_in}
              </a>
              {ctaPrimary ? (
                <a
                  href="#pricing"
                  onClick={() => setMenuOpen(false)}
                  className="btn btn-primary"
                  style={{ display: 'block', textAlign: 'center', padding: '12px 18px' }}
                >
                  {ctaPrimary}
                </a>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
